/* eslint-disable react/jsx-no-useless-fragment */
import LinkButton from '@/components/atoms/LinkButton';
import { useRecoilState, useRecoilValue } from 'recoil';
import { refundBillingState } from '@/mobile/states/refundBillingState';
import billingDataState from '@/states/billingDataState';
import { useEffect } from 'react';
import { ApiUrl } from '@/utils/constants';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import useSetLoading from '@/hooks/useSetLoading';
import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import url from '@/utils/config';
import Plan from './Plan';
import Billing from './Billing';
import Heading from '../atoms/Heading';

type Props = {
  anchor: string;
  plan: string;
  customerNumber: string;
  isOpen: boolean;
  isMobile?: boolean;
};

/**
 * 利用料金一覧と返還請求導線、契約プラン表示をまとめて扱う請求情報セクション。
 * モバイルと固定で見せる導線が異なるため、請求データ取得と表示条件をこの部品で吸収している。
 */

const BillingInfo = ({ anchor, plan, customerNumber, isOpen, isMobile = false }: Props) => {
  const refundBillingData = useRecoilValue(refundBillingState);
  const [billingData, setBillingData] = useRecoilState(billingDataState);
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();
  const { startLoading, finishLoading } = useSetLoading();

  const apiUrl = ApiUrl.Read.BILLING_INFO_V2;

  const fetchBillingData = async () => {
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
    startLoading(apiUrlWithServiceId);

    const headers = await getSigV4HeadersForGet(apiUrlWithServiceId);
    await api
      .get(apiUrlWithServiceId, headers)
      .then(({ data }) => {
        const { isError } = commonResponseCodeHandling({
          responseCode: data.response_code,
          isTransitionTop: true,
        });

        if (isError) return;

        // 請求一覧は複数部品で参照するため、取得結果を Recoil に保存して再利用する。
        setBillingData(data);
      })
      .catch((error: any) => {
        // ログイントークンの有効期限が切れていたら、ログイン画面に遷移
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }

        commonErrorHandling({ error, isTransitionTop: true });
      })
      .finally(() => finishLoading(apiUrlWithServiceId));
  };

  useEffect(() => {
    fetchBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='price'>
          ご利用料金
        </Heading>
        <Billing isOpen={isOpen} isMobile={isMobile} />
        {isMobile && refundBillingData.can_display && (
          // モバイルは返還請求明細を別画面で出すため、条件に応じて専用導線を表示する。
          <div className='m-button col2'>
            <LinkButton variant='secondary' href='/mobile/top/reimbursement/'>
              返還請求明細
            </LinkButton>
          </div>
        )}
        {!isMobile && billingData.billing_list.length > 0 && (
          // 固定回線側は一覧画面への導線を追加し、カルーセル外からも履歴へ進めるようにする。
          <div className='m-button align-center'>
            <LinkButton variant='secondary' href='/top/history/'>
              ご利用料金・お支払い状況一覧
            </LinkButton>
          </div>
        )}
        <Plan plan={plan} customerNumber={customerNumber} />
      </div>
    </div>
  );
};

BillingInfo.defaultProps = {
  isMobile: false,
};

export default BillingInfo;
